/**
 * Persistência local de rascunhos de Máscaras de Atendimento.
 * Chave: médico + contexto (nova | templateId).
 */

import type { AttendanceTemplateContent } from '@/services/attendanceTemplates';

export type AttendanceMaskDraftTabId =
  | 'chiefComplaint'
  | 'hda'
  | 'physicalExam'
  | 'hypothesis'
  | 'conduct'
  | 'observations';

export type AttendanceMaskDraft = {
  doctorId: string;
  /** null = criação de nova máscara */
  templateId: string | null;
  updatedAt: string;
  name: string;
  category: string;
  isFeatured: boolean;
  content: AttendanceTemplateContent;
  activeTab?: AttendanceMaskDraftTabId;
};

const STORAGE_PREFIX = 'dh:attendance-mask-draft';

export function buildAttendanceMaskDraftKey(
  doctorId: string,
  templateId: string | null | undefined,
): string {
  const scope = templateId && templateId.trim() ? templateId.trim() : 'new';
  return `${STORAGE_PREFIX}:${doctorId}:${scope}`;
}

export function isAttendanceMaskDraftEmpty(draft: Pick<AttendanceMaskDraft, 'name' | 'category' | 'content'>): boolean {
  const nameEmpty = !draft.name?.trim();
  const categoryEmpty = !draft.category?.trim();
  const content = draft.content || {};
  const contentEmpty = [
    content.chiefComplaint,
    content.hda,
    content.physicalExam,
    content.hypothesis,
    content.conduct,
    content.observations,
  ].every((v) => !String(v || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim());

  return nameEmpty && categoryEmpty && contentEmpty;
}

export function saveAttendanceMaskDraft(draft: AttendanceMaskDraft): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (!draft.doctorId) return;

  // Não persiste rascunho completamente vazio (evita lixo)
  if (isAttendanceMaskDraftEmpty(draft)) {
    clearAttendanceMaskDraft(draft.doctorId, draft.templateId);
    return;
  }

  const key = buildAttendanceMaskDraftKey(draft.doctorId, draft.templateId);
  const payload: AttendanceMaskDraft = {
    ...draft,
    updatedAt: draft.updatedAt || new Date().toISOString(),
  };
  window.localStorage.setItem(key, JSON.stringify(payload));
}

export function loadAttendanceMaskDraft(
  doctorId: string,
  templateId: string | null | undefined,
): AttendanceMaskDraft | null {
  if (typeof window === 'undefined' || !window.localStorage || !doctorId) return null;

  try {
    const raw = window.localStorage.getItem(buildAttendanceMaskDraftKey(doctorId, templateId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AttendanceMaskDraft;
    if (!parsed || parsed.doctorId !== doctorId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearAttendanceMaskDraft(
  doctorId: string,
  templateId: string | null | undefined,
): void {
  if (typeof window === 'undefined' || !window.localStorage || !doctorId) return;
  window.localStorage.removeItem(buildAttendanceMaskDraftKey(doctorId, templateId));
}

/**
 * Decide se o rascunho local deve prevalecer sobre o template do servidor.
 * Regra: draft mais recente (updatedAt) vence.
 */
export function shouldPreferLocalDraft(
  draft: AttendanceMaskDraft | null,
  serverUpdatedAt?: string | null,
): boolean {
  if (!draft) return false;
  if (!serverUpdatedAt) return true;
  const draftTs = Date.parse(draft.updatedAt);
  const serverTs = Date.parse(serverUpdatedAt);
  if (Number.isNaN(draftTs)) return true;
  if (Number.isNaN(serverTs)) return true;
  return draftTs >= serverTs;
}

export function createDebouncedSave(
  delayMs: number,
  saveFn: (draft: AttendanceMaskDraft) => void,
): {
  schedule: (draft: AttendanceMaskDraft) => void;
  flush: (draft: AttendanceMaskDraft) => void;
  cancel: () => void;
} {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const flush = (draft: AttendanceMaskDraft) => {
    cancel();
    saveFn(draft);
  };

  const schedule = (draft: AttendanceMaskDraft) => {
    cancel();
    timer = setTimeout(() => {
      timer = null;
      saveFn(draft);
    }, delayMs);
  };

  return { schedule, flush, cancel };
}
